import imageStreamUtil from '@/components/layout/imageStream/js/util'
import * as storage from '@/modules/auth/storage'
import { infraMixin } from '@/modules/infra/mixins'
import util from '@/util'
import constants from '@/util/constants'
import { obterImagem } from '@/util/imagem'
import mensagens from '@/util/mensagens'
import { BSpinner } from 'bootstrap-vue'
import { mapActions, mapState } from 'vuex'
import cookieUtil from '@/util/cookieUtil'

const MENSAGEM_POR_DESFECHO = {
  codigo_invalido: mensagens.ssoCodigoInvalido,
  state_invalido: mensagens.ssoRetornoJaProcessado,
  sem_vinculo: mensagens.ssoSemVinculo,
  sem_contexto: mensagens.ssoSemVinculo,
  provedor_indisponivel: mensagens.ssoProvedorIndisponivel,
  acesso_negado: mensagens.ssoAcessoNegado,
  vinculo_divergente: mensagens.ssoVinculoDivergente
}

export default {
  name: 'SsoRetorno',
  components: {
    'b-spinner': BSpinner
  },
  mixins: [infraMixin],
  computed: {
    ...mapState('auth', ['logoCliente']),
    obterLogo () {
      return obterImagem('logo-office300.png')
    }
  },
  data: () => ({
    flags: {
      processando: true
    },
    temLogoClienteNaSessao: false,
    mensagemErro: '',
    callbackAposLoginSucesso: null
  }),

  async mounted () {
    this.temLogoClienteNaSessao = !util.valorVazio(this.logoCliente)
    await this.tratarRetorno()
  },

  methods: {
    ...mapActions('auth', [
      'ActionSignOut',
      'ActionSetLoginStrategy',
      'ActionSetLogoCliente',
      'ActionSetEscritorio',
      'ActionSetTipoDeAcesso',
      'ActionSetPerfilUsuario',
      'ActionSetAcessoFinanceiro',
      'ActionSetAcessoFaturamento',
      'ActionSetAcessoEscritorio',
      'ActionSetFinanceiroContratado',
      'ActionSetFaturamentoContratado',
      'ActionSetNomeUsuarioLogado',
      'ActionSetCodigoFotoUsuario',
      'ActionSetEscritorioInadimplente',
      'ActionSetIdUsuarioLogado',
      'ActionSetDataCriacaoUsuarioLogado',
      'ActionSetEmailUsuario',
      'ActionSetSuporteOnlineHabilitado',
      'ActionSetIdEmpresa',
      'ActionSetOcultarOpcaoMenuBanco',
      'ActionSetRegrasLogin',
      'ActionSetAcessoRelatorios',
      'ActionSetRelatoriosContratado',
      'ActionSetAlertaStatusEmpresa'
    ]),

    async tratarRetorno () {
      const desfecho = this.$route.query.desfecho

      if (desfecho !== 'sucesso') {
        this.apresentarFalha(desfecho)
        return
      }

      const dadosLogin = await this.obterResultadoLogin()
      if (util.valorEhNulo(dadosLogin)) {
        this.apresentarFalha('state_invalido')
        return
      }

      if (dadosLogin.two_factor_required) {
        this.registrarPendencia2fa(dadosLogin)
        this.$router.push({ name: 'validacao-2fa-office' })
        return
      }

      if (dadosLogin.login_rules) {
        this.registrarPendenciasLogin(dadosLogin)
      }

      if (!dadosLogin.success) {
        this.mensagemErro = dadosLogin.message || mensagens.ssoAcessoNegado
        this.flags.processando = false
        return
      }

      this.ActionSignOut()
      await this.definirDadosDeAcesso(dadosLogin)
      await this.encaminharParaTelainicial(dadosLogin)
    },

    async obterResultadoLogin () {
      try {
        await cookieUtil.gerarCookieTokenCsrf()
        const resposta = await this.axios.get('/sso/login/resultado')
        return resposta.data
      } catch (e) {
        return null
      }
    },

    apresentarFalha (desfecho) {
      this.mensagemErro = MENSAGEM_POR_DESFECHO[desfecho] || mensagens.ssoAcessoNegado
      this.flags.processando = false
    },

    registrarPendencia2fa (dadosLogin) {
      this.ActionSetLoginStrategy(dadosLogin.login_strategy)

      const pendenciasLogin = {
        deveValidar2fa: true,
        userId: dadosLogin.user_id,
        tempoExpiracao: dadosLogin.two_factor_expires_in || 30,
        tenant: this.obterEscritorioDoLogin(dadosLogin),
        origemSso: true
      }

      this.ActionSetRegrasLogin(JSON.stringify(pendenciasLogin))
    },

    registrarPendenciasLogin (dadosLogin) {
      this.ActionSetLoginStrategy(dadosLogin.login_strategy)

      const pendenciasLogin = dadosLogin.login_rules

      this.ActionSetRegrasLogin(JSON.stringify(pendenciasLogin))
    },

    obterEscritorioDoLogin (dadosLogin) {
      // o servidor devolve o escritório do fluxo; o localStorage pode ter sido sobrescrito por outra aba
      return dadosLogin.codigo_escritorio || storage.getEscritorio() || ''
    },

    async definirDadosDeAcesso (dadosLogin) {
      const escritorio = this.obterEscritorioDoLogin(dadosLogin)
      this.ActionSetLoginStrategy(dadosLogin.login_strategy)
      this.ActionSetLogoCliente(dadosLogin.f_client_logo)
      this.ActionSetEscritorio(escritorio)
      this.ActionSetTipoDeAcesso(dadosLogin.f_role)
      this.ActionSetPerfilUsuario(dadosLogin.f_profile)
      this.ActionSetAcessoFaturamento(dadosLogin.f_billing)
      this.ActionSetAcessoEscritorio(dadosLogin.f_office)
      this.ActionSetFaturamentoContratado(dadosLogin.f_billing_contract)
      this.ActionSetAcessoFinanceiro(dadosLogin.f_financial)
      this.ActionSetFinanceiroContratado(dadosLogin.f_financial_contract)
      this.ActionSetAcessoRelatorios(dadosLogin.f_reports)
      this.ActionSetRelatoriosContratado(dadosLogin.f_reports_contract)
      this.ActionSetNomeUsuarioLogado(dadosLogin.f_user_name)
      this.ActionSetEmailUsuario(dadosLogin.user_mail)
      this.ActionSetIdUsuarioLogado(dadosLogin.user_id)
      this.ActionSetDataCriacaoUsuarioLogado(dadosLogin.user_create_date)
      const codigoFotoUsuario = await this.obterCodigoFotoUsuario(dadosLogin.f_photo)
      this.ActionSetCodigoFotoUsuario(codigoFotoUsuario)
      this.ActionSetEscritorioInadimplente(dadosLogin.escritorioInadimplente)
      this.ActionSetAlertaStatusEmpresa(dadosLogin.alertaStatusEmpresa ?? null)
      this.ActionSetSuporteOnlineHabilitado(dadosLogin.onlineSupportEnabled)
      this.ActionSetIdEmpresa(dadosLogin.office_id)
      this.ActionSetOcultarOpcaoMenuBanco(dadosLogin.hideBankMenu)
    },

    async obterCodigoFotoUsuario (streamFotoUsuario) {
      try {
        return await imageStreamUtil.obterCodigoBase64DoObjetoStream(streamFotoUsuario)
      } catch (e) {
        return null
      }
    },

    async encaminharParaTelainicial (dadosLogin) {
      const listaPerfisAcessoDiretoJuridico = this.obterListaPerfisAcessoDiretoJuridico()
      const perfilUsuario = dadosLogin.f_profile.toString()
      const perfilPodeAcessarDiretoJuridico = (listaPerfisAcessoDiretoJuridico.indexOf(perfilUsuario) !== -1)
      // sem desvio para primeiro acesso ou troca de senha: a senha local não é o meio de entrada de quem veio pelo provedor
      const rotaInicial = 'inicio'

      if (perfilPodeAcessarDiretoJuridico) {
        await this.redirecionarParaSistemaExterno(constants.urlSistemaExterno.juridico, false)
        this.ActionSignOut()
      } else {
        this.redirecionarParaManager(rotaInicial, () => {
          if (this.callbackAposLoginSucesso !== null) {
            this.callbackAposLoginSucesso()
          }
        })
      }
    }
  }
}
